import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import UserMenu from './UserMenu';
import ProjectTypeModal from './ProjectTypeModal';
import { projectAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { 
  Plus, 
  Video,
  Folder,
  Play
} from 'lucide-react';


const TopazHero = ({ onNewProject }) => (
  <div className="relative w-full rounded-2xl overflow-hidden mb-8" style={{backgroundColor: '#141414', minHeight: '360px'}}>
    {/* Background Image */}
    <div 
      className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-70"
      style={{
        backgroundImage: 'url(/hero-bg.png)',
        backgroundPosition: 'center center',
        backgroundSize: 'cover'
      }}
    />
    {/* Dark overlay for better text readability */}
    <div className="absolute inset-0 bg-black/40" />
    
    <div className="absolute inset-0 flex items-center">
      <div className="ml-16">
        <h1 className="text-3xl font-bold text-white mb-3">FnctionOS</h1>
        <div className="text-sm text-gray-200 mb-6">
          <div>Upload your first video</div>
          <div style={{paddingLeft: '0.1em'}}>to get started with AI-powered editing!</div>
        </div>
        <Button 
          onClick={onNewProject}
          className="text-white font-medium px-5 py-2 text-xs shadow-lg hover:shadow-xl transition-all duration-200"
          style={{backgroundColor: '#0569ff'}}
        >
          Start New Project
        </Button>
      </div>
    </div>
  </div>
);

const ProjectCard = ({ project, onOpen }) => (
  <div className="relative group rounded-lg overflow-hidden transition-all duration-200 hover:scale-105" style={{backgroundColor: '#141414', minHeight: '200px'}}>
    <div className="aspect-video bg-zinc-800 flex items-center justify-center">
      {project.thumbnail ? (
        <img src={project.thumbnail} alt={project.name} className="w-full h-full object-cover" />
      ) : (
        <Folder className="w-12 h-12 text-gray-500" />
      )}
    </div>
    <div className="p-4 text-center">
      <h4 className="text-white font-medium text-sm mb-2 truncate">{project.name}</h4>
      <p className="text-gray-400 text-xs">Edited on {new Date(project.created_at).toLocaleDateString()}</p>
    </div>
    
    {/* Hover OPEN button */}
    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
      <Button 
        onClick={() => onOpen(project)}
        className="text-white font-medium px-3 py-1 text-xs rounded transition-colors"
        style={{backgroundColor: '#0569ff'}}
      >
        Open
      </Button>
    </div>
  </div>
);

const EmptyState = ({ onNewProject }) => (
  <div className="text-center p-8 border-2 border-dashed border-zinc-700 rounded-lg" style={{backgroundColor: '#141414'}}>
    <Video className="w-8 h-8 mx-auto mb-3 text-gray-500" />
    <h3 className="text-lg font-semibold text-white mb-2">No projects yet</h3>
    <p className="text-gray-400 mb-4 text-sm">
      Upload your first video to get started with AI-powered editing!
    </p>
    <Button 
      onClick={onNewProject}
      className="bg-gradient-to-r from-purple-600 to-pink-600 text-white"
    >
      <Plus className="w-3 h-3 mr-2" />
      Create Your First Project
    </Button>
  </div>
);

const Dashboard = ({ user, onLogout, onShowSettings, onNewProject, onProjectSelect }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProjectTypeModal, setShowProjectTypeModal] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await projectAPI.getProjects();
      setProjects(projectsData || []);
    } catch (error) {
      console.error('Failed to load projects:', error);
      toast({
        variant: "destructive",
        title: "Failed to load projects",
        description: "Please refresh the page to try again.",
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewProject = () => {
    setShowProjectTypeModal(true);
  };

  const handleProjectTypeSelect = async (projectType) => {
    setShowProjectTypeModal(false);
    
    try {
      // Create a new project with the selected type
      const newProject = await projectAPI.createProject({
        name: `New ${projectType === 'short-form' ? 'Short Form' : 'Long Form'} Project`,
        type: projectType,
        status: 'draft'
      });

      toast({
        title: "Project created!",
        description: `Your ${projectType === 'short-form' ? 'short form' : 'long form'} project has been created.`,
      });

      // Navigate to the video editor with the new project
      if (onNewProject) {
        onNewProject(newProject);
      }

    } catch (error) {
      console.error('Failed to create project:', error);
      toast({
        variant: "destructive",
        title: "Failed to create project",
        description: "Please try again.",
      });
    }
  };

  return (
    <div className="flex h-screen text-white" style={{backgroundColor: '#0d0d0d'}}>
      <Sidebar />
      <main className="flex-1 p-6 overflow-y-auto" style={{backgroundColor: '#0d0d0d'}}>
        <header className="flex justify-end items-center mb-6 space-x-4">
            <Button 
              variant="outline" 
              className="bg-blue-500 text-white hover:bg-blue-600 rounded-full px-2 py-1 text-xs shadow-lg"
              style={{
                backgroundColor: '#0569ff',
                boxShadow: '0 0 20px rgba(5, 105, 255, 0.4), 0 0 40px rgba(5, 105, 255, 0.2)'
              }}
            >
                ◆ Upgrade
            </Button>
             {user && (
               <UserMenu 
                 user={user}
                 onShowSettings={onShowSettings}
                 onLogout={onLogout}
               />
             )}
        </header>

        <TopazHero onNewProject={handleNewProject} />


        <div>
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-lg font-semibold">My files</h2>
            <a href="#" className="text-sm text-gray-400 hover:text-white">See all</a>
          </div>

          {loading ? (
             <p className="text-gray-400">Loading projects...</p>
          ) : projects.length === 0 ? (
            <EmptyState onNewProject={handleNewProject} />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} onOpen={onProjectSelect || (() => {})} />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Project Type Selection Modal */}
      <ProjectTypeModal 
        isOpen={showProjectTypeModal}
        onClose={() => setShowProjectTypeModal(false)}
        onSelectType={handleProjectTypeSelect}
      />
    </div>
  );
};

export default Dashboard;